// ** react imports
import React from 'react';
import PropTypes from 'prop-types';

// ** views imports
import UsersTableItem from './UsersTableItem';

// ** components imports
import Loading from '../../../components/common/Loading';

function UsersTable(props) {
  // props
  const {
    data,
    isGetAllUsersLoading,
    isGetAllUsersFetching,
    onSortChange,
    currentSortBy,
    currentSortOrder,
    selectedItems,
  } = props;

  const isLoading = isGetAllUsersLoading || isGetAllUsersFetching;

  // Handle sort click
  const handleSortClick = field => {
    if (onSortChange) {
      const newSortOrder =
        currentSortBy === field && currentSortOrder === 'asc' ? 'desc' : 'asc';
      onSortChange(field, newSortOrder);
    }
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (currentSortBy !== field) {
      return (
        <svg
          className="w-3 h-3 ml-1 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return currentSortOrder === 'asc' ? (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  return (
    <div className="bg-white h-[calc(70vh)] dark:bg-gray-800 shadow-xs rounded-xl relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading />
          </div>
        </div>
      )}

      <div>
        <header className="px-5 py-4 bg-white dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/60">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">
            All Users{' '}
            <span className="text-gray-400 dark:text-gray-400 font-medium">
              {data?.total || 0}
            </span>
          </h2>
        </header>
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-gray-300">
            <thead className="sticky top-0 z-10 text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('userId')}
                  >
                    User ID
                    <SortIcon field="userId" />
                  </button>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('fullName')}
                  >
                    Full Name
                    <SortIcon field="fullName" />
                  </button>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('email')}
                  >
                    Email
                    <SortIcon field="email" />
                  </button>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase flex items-center justify-center">
                  <button
                    className="font-semibold text-center whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('phone')}
                  >
                    Phone Number
                    <SortIcon field="phone" />
                  </button>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('role')}
                  >
                    Role
                    <SortIcon field="role" />
                  </button>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase">
                  <div className="font-semibold text-left">Status</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap uppercase">
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('createdAt')}
                  >
                    Created Date
                    <SortIcon field="createdAt" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-700/60">
              {data?.data && data.data.length > 0 ? (
                data.data.map(user => {
                  return (
                    <UsersTableItem
                      data={user}
                      key={user._id}
                      isSelected={
                        Array.isArray(selectedItems) &&
                        selectedItems.includes(user._id)
                      }
                    />
                  );
                })
              ) : !isLoading ? (
                <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <td colSpan="8" className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        No users found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Try adjusting your filters or search criteria to find
                        what you&apos;re looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// PropTypes for better type checking
UsersTable.propTypes = {
  data: PropTypes.object,
  isGetAllUsersLoading: PropTypes.bool,
  isGetAllUsersFetching: PropTypes.bool,
  onSortChange: PropTypes.func,
  currentSortBy: PropTypes.string,
  currentSortOrder: PropTypes.string,
  selectedItems: PropTypes.array,
};

export default UsersTable;
